import type { Role } from './port.js';

/**
 * Sessao do operador — lida do backend, nunca inventada aqui.
 *
 * O navegador nao ve token nenhum: o middleware guarda a sessao e o cookie
 * `mp_console` (httpOnly) so carrega um id opaco. Entao tudo o que o console
 * sabe sobre quem esta logado vem desta chamada.
 */
export interface Sessao {
  username: string;
  email: string | null;
  roles: Role[];
  expiresAt: string;
}

/**
 * Em que pe esta a autenticacao.
 *
 * A distincao entre `anonimo` e `desligado` NAO e detalhe: com o Keycloak
 * desligado nao existe rota de login para onde mandar ninguem. Tratar os dois
 * como "faca login" prenderia o console numa tela que leva a lugar nenhum.
 */
export type EstadoAuth =
  | { tipo: 'autenticado'; sessao: Sessao }
  | { tipo: 'anonimo' }
  | { tipo: 'desligado' }
  | {
      tipo: 'indisponivel';
      motivo: string;
      /**
       * A pagina esta num host e a API em outro.
       *
       * Quando isso acontece o bloqueio e CERTO, e a correcao tambem: e a
       * mesma pagina no host da API. Carregar a URL certa resolve, entao o
       * console leva o operador para la em vez de explicar o que ele deveria
       * ter digitado.
       */
      urlCorrigida?: string;
    };

const PAPEIS_CONHECIDOS: readonly Role[] = ['console-admin', 'console-operador', 'console-leitura'];

function papeis(bruto: unknown): Role[] {
  if (!Array.isArray(bruto)) return [];
  // Filtra para os papeis que o console conhece: um papel alheio vindo do
  // realm nunca deve virar permissao aqui.
  return bruto.filter((papel): papel is Role => PAPEIS_CONHECIDOS.includes(papel as Role));
}

/**
 * Teto para a consulta de sessao.
 *
 * Sem isto, um middleware que ACEITA a conexao e nunca responde deixa o
 * console parado em "verificando sessão…" para sempre — pior que o erro, que
 * ao menos diz o que fazer. Rede recusada falha sozinha; travada, nao.
 */
const TIMEOUT_MS = 8000;

export interface ClienteSessaoOpcoes {
  baseUrl: string;
  /** Injetavel para teste; por padrao o `fetch` do navegador. */
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
}

/**
 * Cliente das rotas /auth do middleware.
 *
 * `credentials: 'include'` em todas as chamadas: sem isso o cookie de sessao
 * nao acompanha a requisicao entre origens (console em :5173, API em :3000) e
 * o operador logado apareceria como anonimo.
 */
export class ClienteSessao {
  private readonly baseUrl: string;
  private readonly buscar: typeof fetch;
  private readonly timeoutMs: number;

  constructor(opcoes: ClienteSessaoOpcoes) {
    this.baseUrl = opcoes.baseUrl.replace(/\/+$/, '');
    this.buscar = opcoes.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.timeoutMs = opcoes.timeoutMs ?? TIMEOUT_MS;
  }

  /** Aborta sozinho depois do teto, para nenhuma tela ficar esperando sem fim. */
  private comTeto(): { signal: AbortSignal; encerrar: () => void } {
    const controlador = new AbortController();
    const id = setTimeout(() => controlador.abort(), this.timeoutMs);
    return { signal: controlador.signal, encerrar: () => clearTimeout(id) };
  }

  async carregar(): Promise<EstadoAuth> {
    const teto = this.comTeto();
    let resposta: Response;
    try {
      resposta = await this.buscar(`${this.baseUrl}/auth/sessao`, {
        credentials: 'include',
        headers: { accept: 'application/json' },
        signal: teto.signal,
      });
    } catch {
      // Nao e "sem sessao": dizer ao operador para logar quando o problema e a
      // rede manda ele para um beco.
      //
      // O erro do `fetch` NAO diz se foi CORS ou servidor fora — o navegador
      // omite isso de proposito, senao qualquer site sondaria a rede interna
      // da maquina. Entao, daqui de dentro, os dois casos sao identicos.
      //
      // Mas da para DEDUZIR: se a origem da pagina e a da API tem hosts
      // diferentes, o navegador vai bloquear mesmo com o servidor no ar. Nesse
      // caso a causa e certa, nao um palpite — e mandar conferir se o servico
      // subiu seria mandar procurar no lugar errado.
      return this.falha();
    } finally {
      teto.encerrar();
    }

    // 404: as rotas /auth nem existem — KEYCLOAK_ENABLED=false.
    if (resposta.status === 404) return { tipo: 'desligado' };
    if (resposta.status === 401) return { tipo: 'anonimo' };

    if (!resposta.ok) {
      return { tipo: 'indisponivel', motivo: `o middleware respondeu ${resposta.status}` };
    }

    const corpo = (await resposta.json().catch(() => null)) as {
      data?: { username?: unknown; email?: unknown; roles?: unknown; expiresAt?: unknown };
    } | null;

    const dados = corpo?.data;
    if (typeof dados?.username !== 'string') {
      return { tipo: 'indisponivel', motivo: 'resposta de sessao malformada' };
    }

    return {
      tipo: 'autenticado',
      sessao: {
        username: dados.username,
        email: typeof dados.email === 'string' ? dados.email : null,
        roles: papeis(dados.roles),
        expiresAt: typeof dados.expiresAt === 'string' ? dados.expiresAt : '',
      },
    };
  }

  /**
   * Por que a chamada falhou — deduzido, nao adivinhado.
   *
   * Compara a origem da pagina com a da API. Hosts diferentes e bloqueio certo
   * do navegador; iguais, sobra rede ou servidor fora.
   */
  private falha(): EstadoAuth {
    const local = globalThis.location;
    if (!local?.origin) return { tipo: 'indisponivel', motivo: 'o middleware nao respondeu' };

    const daPagina = new URL(local.origin);
    const hostApi = new URL(this.baseUrl).hostname;

    if (daPagina.hostname === hostApi) {
      return { tipo: 'indisponivel', motivo: 'o middleware nao respondeu' };
    }

    // Mesma pagina, mesma porta — so o host da API. E o endereco que teria
    // funcionado, entao da para ir direto para ele.
    const corrigida = new URL(local.href);
    corrigida.hostname = hostApi;

    return {
      tipo: 'indisponivel',
      motivo:
        `o navegador bloqueou a resposta — a página está em "${daPagina.hostname}" e a API em ` +
        `"${hostApi}". Não é o servidor: ele responde, mas o navegador descarta a resposta ` +
        'por virem de origens diferentes',
      urlCorrigida: corrigida.href,
    };
  }

  /** Para onde mandar o navegador para logar. */
  urlDeLogin(returnTo = '/'): string {
    return `${this.baseUrl}/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
  }

  /**
   * Encerra a sessao aqui e devolve para onde ir para encerrar no Keycloak.
   *
   * Sair so daqui deixaria a sessao SSO viva: o proximo /auth/login entraria
   * de novo sem pedir nada, e o operador acharia que saiu quando nao saiu.
   */
  async sair(): Promise<string | null> {
    // `comTeto()` devolve o sinal E o cancelador. Usar so o sinal deixava um
    // setTimeout de 8s vivo mesmo quando a resposta voltava em 50ms — o mesmo
    // descuido que `carregar()` evita com o `finally`.
    const teto = this.comTeto();
    try {
      const resposta = await this.buscar(`${this.baseUrl}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { accept: 'application/json' },
        signal: teto.signal,
      });
      if (!resposta.ok) return null;
      const corpo = (await resposta.json().catch(() => null)) as {
        data?: { logoutUrl?: unknown };
      } | null;
      const url = corpo?.data?.logoutUrl;
      return typeof url === 'string' ? url : null;
    } catch {
      return null;
    } finally {
      teto.encerrar();
    }
  }
}
